// Route: /courses — список курсов (LEARNING-SYSTEM-UI §3.1).
// URL state: ?status=active|completed|archived|all, ?q=, ?demo=
//
// Раскладку по вкладкам решает ОДНА функция `courseShelf` (course-shelf.ts), а
// не условия по месту: пройденный курс обязан уходить из «Активных» во вкладку
// «Завершённые», иначе список навсегда показывает то, что человек уже закрыл.
//
// Курс — главная единица обучения: карточка ведёт СРАЗУ в текущую тему,
// а не в оглавление. «Мой курс» существует всегда: при пустом списке
// вызываем courses.ensureDefault один раз, чтобы пользователю было куда
// складывать материалы без обязательного шага «создай курс».

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { Button } from "@studyhub/ui/components/button";
import { EmptyState } from "@studyhub/ui/components/empty-state";
import { ErrorState } from "@studyhub/ui/components/error-state";
import { SegmentedControl } from "@studyhub/ui/components/segmented-control";
import { SectionHeader } from "@studyhub/ui/components/studyhub/section-header";
import { IconDots, IconLayoutGrid, IconPlus, IconRoute, IconSchool } from "@tabler/icons-react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useCourses, useEnsureDefaultCourse } from "@/components/courses/_api";
import { CourseCard } from "@/components/courses/course-card";
import { courseShelf } from "@/components/courses/course-shelf";
import { SECTION_ICONS } from "@/components/navigation/section-icons";
import { ActionMenu } from "@/components/shared/action-menu";
import {
	CreateEmptyTile,
	LIST_PAGE_GRID,
	LIST_PAGE_SHELL,
	LIST_PAGE_STACK,
	ListToolbar,
} from "@/components/shared/entity-list";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import {
	SectionIntro,
	SectionIntroReopen,
	useSectionIntro,
} from "@/components/shared/section-intro";
import { useViewMode } from "@/hooks/use-view-mode";
import { useCourseCreatePathStore } from "@/stores/course-create-path";

const searchSchema = z.object({
	status: z.enum(["active", "completed", "archived", "all"]).optional(),
	q: z.string().optional(),
	demo: z.enum(["default", "loading", "empty", "error"]).optional(),
});

export const Route = createFileRoute("/courses/")({
	validateSearch: tolerantSearch(searchSchema),
	component: CoursesListPage,
	pendingComponent: PagePendingFallback,
});

function CoursesListPage(): React.ReactElement {
	const { t } = useTranslation("courses");
	const intro = useSectionIntro("courses");
	const search = Route.useSearch();
	const navigate = useNavigate({ from: Route.fullPath });
	const [view, setView] = useViewMode("courses");
	// «Новый курс» ведёт в развилку сценариев (COURSE-CREATION-FLOW §4.1), а не
	// сразу в форму «название + дата экзамена»: у новичка нет своих файлов, и
	// прежний путь высаживал его на пустой экран с требованием их загрузить.
	// Диалог глобальный (его же открывает карточка «Курс» в меню «+»), поэтому
	// здесь только команда открыть — монтировать второй экземпляр нельзя.
	const openCreatePath = useCourseCreatePathStore((s) => s.open);

	const coursesQuery = useCourses();
	const ensureDefault = useEnsureDefaultCourse();
	const ensuredRef = useRef(false);

	const status = search.status ?? "active";
	const q = search.q ?? "";

	function updateSearch(next: { status?: typeof status; q?: string }): void {
		void navigate({
			search: (prev) => ({
				...prev,
				status: (next.status ?? status) === "active" ? undefined : (next.status ?? status),
				q: (next.q ?? q) || undefined,
			}),
			replace: true,
		});
	}

	const all = coursesQuery.items;

	// Один проход по списку вместо трёх `filter`: полка курса вычисляется ровно
	// раз и переиспользуется и счётчиками, и фильтром — разъехаться им негде.
	const shelves = useMemo(() => new Map(all.map((c) => [c.id, courseShelf(c)])), [all]);

	const counts = useMemo(() => {
		const out = { all: all.length, active: 0, completed: 0, archived: 0 };
		for (const c of all) out[shelves.get(c.id) ?? "active"] += 1;
		return out;
	}, [all, shelves]);

	const courses = useMemo(() => {
		let items = search.demo === "empty" ? [] : all;
		if (status !== "all") items = items.filter((c) => shelves.get(c.id) === status);
		if (q) {
			const needle = q.toLowerCase();
			items = items.filter((c) => c.title.toLowerCase().includes(needle));
		}
		return items.toSorted((a, b) => b.updatedAt.localeCompare(a.updatedAt));
	}, [all, shelves, status, q, search.demo]);

	const isLoading = search.demo === "loading" || coursesQuery.isLoading;
	const isError = search.demo === "error" || coursesQuery.isError;

	// «Мой курс» существует всегда: один раз на сессию создаём дефолтный курс,
	// если у пользователя нет ни одного. Ошибку глушим — экран остаётся с
	// EmptyState и явной кнопкой «Создать курс».
	useEffect(() => {
		if (isLoading || isError || ensuredRef.current) return;
		if (all.length > 0 || search.demo != null) return;
		ensuredRef.current = true;
		ensureDefault.mutate(undefined as never);
	}, [isLoading, isError, all.length, search.demo, ensureDefault]);

	const isEnsuring = ensureDefault.isPending;
	// Курсов нет вовсе (≠ «фильтр ничего не нашёл») — разные тексты пустого экрана.
	const noCoursesAtAll = all.length === 0 || search.demo === "empty";
	const CalendarIcon = SECTION_ICONS.studyCalendar;

	return (
		<div className={LIST_PAGE_SHELL}>
			<SectionHeader
				category="ai"
				icon={<IconRoute stroke={1.75} />}
				title={t("title")}
				description={t("subtitle")}
				actions={
					<>
						<SectionIntroReopen intro={intro} />
						<Button onClick={() => openCreatePath("courses-list")} className="cursor-pointer">
							<IconPlus size={14} />
							<span className="ml-1.5 hidden sm:inline">{t("list.create")}</span>
						</Button>
						{/* Каталог — второй по частоте сценарий после «создать», поэтому он
						    в шапке, а не за `⋮`: спрятанный вход в каталог никто не находит.
						    Он же единственный: программы платформы и курсы авторов лежат
						    там вместе, отдельного пункта «Шаблоны программ» больше нет. */}
						<Button asChild variant="outline" className="cursor-pointer">
							<Link to="/catalog" preload="intent">
								<IconSchool size={14} />
								<span className="ml-1.5 hidden sm:inline">{t("list.courseTemplates")}</span>
							</Link>
						</Button>
						<ActionMenu
							align="end"
							title={t("title")}
							items={[
								{
									key: "calendar",
									label: t("list.calendar"),
									icon: <CalendarIcon size={18} />,
									onSelect: () => void navigate({ to: "/courses/calendar" }),
								},
								{
									key: "my-templates",
									label: t("list.myTemplates"),
									icon: <IconLayoutGrid size={18} />,
									onSelect: () =>
										void navigate({
											to: "/catalog",
											search: { scope: "mine" },
										}),
								},
							]}
							trigger={
								<Button
									variant="outline"
									size="icon"
									className="cursor-pointer max-sm:size-9 pointer-coarse:size-11"
									aria-label={t("title")}
								>
									<IconDots size={16} />
								</Button>
							}
						/>
					</>
				}
			/>

			{/* Баннер-объяснение раздела: текст из манифеста модуля, состояние
			    «свёрнут» — серверное (см. section-intro.tsx). */}
			<SectionIntro intro={intro} />

			<ListToolbar
				search={{
					value: q,
					onChange: (next) => updateSearch({ q: next }),
					placeholder: t("list.search"),
				}}
				segments={
					<SegmentedControl
						scrollable
						className="shrink-0"
						aria-label={t("list.title")}
						value={status}
						onValueChange={(next) => updateSearch({ status: next })}
						options={[
							{ value: "active", label: t("list.segments.active"), count: counts.active },
							{
								value: "completed",
								label: t("list.segments.completed"),
								count: counts.completed,
							},
							{ value: "archived", label: t("list.segments.archived"), count: counts.archived },
							{ value: "all", label: t("list.segments.all"), count: counts.all },
						]}
					/>
				}
				view={view}
				onViewChange={setView}
			/>

			{isLoading || (isEnsuring && all.length === 0) ? (
				<div className={LIST_PAGE_GRID}>
					{/* Высота и число плейсхолдеров повторяют реальную сетку карточек
						    С ОБЛОЖКОЙ: прежние 196px были высотой карточки без картинки, и
						    список подпрыгивал на пол-экрана в момент загрузки. */}
					{[0, 1, 2].map((i) => (
						<div
							key={i}
							aria-busy="true"
							className="h-100 animate-pulse rounded-card border border-border bg-card shadow-e1"
						/>
					))}
				</div>
			) : isError ? (
				<ErrorState
					title={t("errors.loadFailed")}
					action={
						<Button
							variant="outline"
							onClick={() => void coursesQuery.refetch()}
							className="cursor-pointer"
						>
							{t("errors.retry")}
						</Button>
					}
				/>
			) : courses.length === 0 ? (
				// «Пусто» и «фильтр ничего не нашёл» — разные тексты (§9 матрица состояний).
				<EmptyState
					icon={<IconRoute stroke={1.5} />}
					title={noCoursesAtAll ? t("list.empty") : t("list.noMatches")}
					description={noCoursesAtAll ? t("list.emptyHint") : undefined}
					action={
						noCoursesAtAll ? (
							<Button onClick={() => openCreatePath("courses-list")} className="cursor-pointer">
								<IconPlus size={14} />
								{t("list.emptyCta")}
							</Button>
						) : (
							<Button
								variant="outline"
								className="cursor-pointer"
								onClick={() => updateSearch({ status: "all", q: "" })}
							>
								{t("list.segments.all")}
							</Button>
						)
					}
				/>
			) : (
				<div className={view === "grid" ? LIST_PAGE_GRID : LIST_PAGE_STACK}>
					{courses.map((course) => (
						<CourseCard key={course.id} course={course} view={view} />
					))}
					{/* Тактильная плитка «Создать курс» — только в grid (как в /flashcards). */}
					{view === "grid" && (
						<CreateEmptyTile
							category="ai"
							icon={<IconRoute stroke={1.75} />}
							label={t("list.create")}
							onClick={() => openCreatePath("courses-list")}
						/>
					)}
				</div>
			)}
		</div>
	);
}
